import React, { useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import { ChevronRightRounded, CloseRounded, ErrorOutlineRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

/** Rows shown before the list collapses behind "Show N more" — a phone screen is short. */
const VISIBLE_ROWS = 5;

/**
 * The banner a form shows when Save is refused: every error at once, each one a button
 * that jumps to its field (switching tab first when the field is on another one).
 *
 * Driven entirely by useValidationSummary's `summaryProps`; it holds no opinion about
 * which errors exist. Inline, not a modal — the person should be able to read it and
 * fix the fields without dismissing anything first.
 *
 * `bannerRef` comes from the hook: it scrolls here when an error has no field to land
 * on, and looks fields up only inside the dialog or form that holds this banner.
 */
export default function FormErrorSummary({
  bannerRef,
  items = [],
  visible = false,
  onSelect,
  onDismiss,
  className = "",
}) {
  const { t } = useTranslation("validation");
  const [expanded, setExpanded] = useState(false);

  // A fresh failed save starts collapsed again.
  useEffect(() => {
    if (!visible) setExpanded(false);
  }, [visible]);

  if (!visible || items.length === 0) return null;

  const shown = expanded ? items : items.slice(0, VISIBLE_ROWS);
  const hidden = items.length - shown.length;

  return (
    <div
      ref={bannerRef}
      data-form-error-summary
      role="alert"
      aria-live="assertive"
      className={`rounded-[12px] border border-rose-200 border-s-[3px] border-s-rose-500 bg-rose-50 px-[12px] py-[10px] shadow-[var(--shadow-xs)] ${className}`}
    >
      <div className="flex items-start gap-[10px]">
        <span className="mt-[1px] flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
          <ErrorOutlineRounded sx={{ fontSize: 18 }} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold leading-snug text-rose-800">
            {t("summaryTitle", { count: items.length })}
          </p>
          <p className="text-[12px] leading-snug text-rose-700/80">{t("summaryHint")}</p>
        </div>
        {onDismiss && (
          <IconButton
            size="small"
            aria-label={t("dismiss")}
            onClick={onDismiss}
            sx={{ p: "3px", color: "#be123c", "&:hover": { backgroundColor: "#ffe4e6" } }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </div>

      <ul className="mt-[8px] grid gap-[2px] ps-[38px]">
        {shown.map((item) => (
          <li key={item.field}>
            <button
              type="button"
              title={t("goToField")}
              onClick={() => onSelect && onSelect(item.field)}
              className="group flex w-full items-center gap-[8px] rounded-[8px] px-[8px] py-[5px] text-start transition-colors hover:bg-rose-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
            >
              {item.sectionLabel && (
                <span className="shrink-0 rounded-full border border-rose-200 bg-white px-[8px] py-[1px] text-[11px] font-medium text-rose-700">
                  {item.sectionLabel}
                </span>
              )}
              <span className="min-w-0 flex-1 text-[13px] leading-snug text-gray-700">
                {item.message}
              </span>
              <ChevronRightRounded
                className="shrink-0 text-rose-400 transition-transform group-hover:translate-x-[2px]"
                sx={{ fontSize: 18 }}
              />
            </button>
          </li>
        ))}
      </ul>

      {(hidden > 0 || expanded) && items.length > VISIBLE_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ms-[46px] mt-[4px] text-[12px] font-medium text-rose-700 hover:underline focus:outline-none focus-visible:underline"
        >
          {expanded ? t("showLess") : t("showMore", { count: hidden })}
        </button>
      )}
    </div>
  );
}
